import React from 'react'
import { faMobile, faTablet, faLaptop, faDesktop, faBrowser, faVrCardboard } from '@fortawesome/pro-solid-svg-icons'
import { faApple, faAndroid, faWindows, faLinux } from '@fortawesome/free-brands-svg-icons'

// A device outline with its maker's mark seated in the screen.
//
// Mirrors caffeine-viewer's CodeAuthenticatorUIV2 so the admin and the apps
// present identically, but the geometry is MEASURED against the artwork THIS
// package ships, not copied from the viewer's. The two are not interchangeable:
// Font Awesome 6's desktop is a 576x512 drawing where 7's is 512x512, so the
// viewer's fractions would land the mark in the wrong place here.
//
// Every number below is the artwork's own bounding box expressed as a
// percentage, with one small optical nudge folded in -- a geometric centre and
// a visually centred mark are not the same point once a stand or a keyboard
// adds mass below the screen.
//
// `plate` fills a screen the outline leaves hollow. It paints BEHIND the
// outline, so the device's own pixels cover any overshoot; it is deliberately
// bled a couple of percent past the measured hole, because undershooting leaves
// a visible light ring inside the screen and overshooting costs nothing.
//
// Do NOT infer which icons need a plate from their path structure. A laptop lid
// is one self-winding contour that still draws a frame, so counting contours
// says "solid" and removing its plate leaves a white mark on a white ground.
const DEVICES = {
  phone: { icon: faMobile, markX: 50, markY: 37.9, markW: 34 },
  tablet: { icon: faTablet, markX: 61.6, markY: 50, markW: 30, rotate: 90 },
  // A landscape tablet reads as a tablet; portrait it is just a large phone.
  // The rotation puts the home pill on the left, so the screen's centre is
  // right of the slab's centre -- markX is past 50 on purpose.

  desktop: {
    icon: faLaptop,
    markX: 50,
    markY: 36.1,
    markW: 26,
    plate: { left: 18, top: 12.3, width: 64, height: 57.6 }
  },
  monitor: {
    icon: faDesktop,
    markX: 50,
    markY: 36.9,
    markW: 30,
    plate: { left: 9.1, top: 10.5, width: 81.8, height: 47.8 }
  },
  web: { icon: faBrowser, markX: 50, markY: 61.4, markW: 30 },
  // The browser's mark sits low because the chrome bar occupies the top; this
  // is the content area's centre, not the frame's.

  vr: { icon: faVrCardboard, markX: 50, markY: 50, markW: 0 }
}

const BRANDS = {
  apple: faApple,
  android: faAndroid,
  windows: faWindows,
  linux: faLinux
}

const pathOf = icon => (Array.isArray(icon.icon[4]) ? icon.icon[4][0] : icon.icon[4])

export const DeviceGlyph = ({ family, brand, size = 26 }) => {
  const device = DEVICES[family] || DEVICES.desktop
  const [vw, vh] = [device.icon.icon[0], device.icon.icon[1]]
  const ratio = vw / vh

  // Rotating swaps the box: the wrapper takes the landscape footprint and the
  // outline inside it keeps its portrait one, so a 90-degree turn lands exactly
  // on the wrapper. Percentages below are then read against what you SEE, which
  // is the only frame "left" and "up" mean anything in.
  const wrapW = device.rotate ? size / ratio : size * ratio
  const wrapH = size
  const svgW = device.rotate ? wrapH : wrapW
  const svgH = device.rotate ? wrapW : wrapH

  const mark = BRANDS[brand]

  return (
    <span className="dg" style={{ width: wrapW, height: wrapH }}>
      {device.plate && (
        <i
          className="dg__plate"
          style={{
            left: `${device.plate.left}%`,
            top: `${device.plate.top}%`,
            width: `${device.plate.width}%`,
            height: `${device.plate.height}%`
          }}
        />
      )}

      <svg
        className={`dg__device${device.rotate ? ' dg__device--rot' : ''}`}
        viewBox={`0 0 ${vw} ${vh}`}
        style={{ width: svgW, height: svgH }}
        aria-hidden="true"
      >
        <path d={pathOf(device.icon)} />
      </svg>

      {mark && device.markW > 0 && (
        <svg
          className="dg__mark"
          viewBox={`0 0 ${mark.icon[0]} ${mark.icon[1]}`}
          style={{ left: `${device.markX}%`, top: `${device.markY}%`, width: `${device.markW}%` }}
          aria-hidden="true"
        >
          <path d={pathOf(mark)} />
        </svg>
      )}
    </span>
  )
}
